import React from 'react';
import { useMutation } from 'react-query';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Button,
  Dialog,
  Checkbox,
  DialogTitle,
  DialogActions,
  DialogContent,
  FormControlLabel,
} from '@mui/material';

import { useDocumentActionsStatus } from '../hooks/DocumentActionsStatus.context';
import { updatePdfDocumentAckDateAsync } from '../pdfDocuments.service';
import { IDocumentVersion } from 'utils/DexieDBManager';
import { RESOURCES_KEYS } from 'utils/i18n/resources';
import { computeErrorMessage } from 'utils/utils';

interface IAcknowledgmentProps {
  documentVersion: IDocumentVersion;
}

const Acknowledgment: React.FunctionComponent<IAcknowledgmentProps> = ({ documentVersion }) => {
  const [isConfirmationReceiptChecked, setIsConfirmationReceiptChecked] = React.useState(false);
  const [isConfirmationReceiptOpen, setIsConfirmationReceiptOpen] = React.useState(true);
  const [hasError, setHasError] = React.useState(false);
  const { dispatch } = useDocumentActionsStatus();

  const { data: updatedPdfDocumentAckData, mutate: updatePdfDocumentAckDate } = useMutation(() =>
    updatePdfDocumentAckDateAsync({
      parentDocumentId: documentVersion.parentDocument.id,
      documentVersionId: documentVersion.id,
      fileId: documentVersion.file.id,
    })
  );

  const { t: translate } = useTranslation();

  const handleClickCancel = () => {
    setIsConfirmationReceiptOpen(false);
  };

  const handleClickSend = () => {
    if (!isConfirmationReceiptChecked) {
      setHasError(true);
    } else {
      updatePdfDocumentAckDate();
    }
  };

  const handleCloseConfirmationReceipt = (_: any, reason: string) => {
    if (!['escapeKeyDown', 'backdropClick'].includes(reason)) {
      setIsConfirmationReceiptOpen(false);
    }
  };

  React.useEffect(() => {
    if (updatedPdfDocumentAckData?.isSuccess) {
      setIsConfirmationReceiptOpen(false);
      dispatch({ type: 'toggleIsAckRequired ', parentDocumentId: documentVersion.parentDocument.id });
    }
  }, [updatedPdfDocumentAckData?.isSuccess, documentVersion.id, dispatch, documentVersion.parentDocument.id]);

  return (
    <Dialog
      sx={{ width: '100%' }}
      open={isConfirmationReceiptOpen}
      onClose={handleCloseConfirmationReceipt}
      aria-labelledby='alert-dialog-title'
      aria-describedby='alert-dialog-description'>
      <DialogTitle id='alert-dialog-title'>{translate(RESOURCES_KEYS.ACK_TITLE)}</DialogTitle>
      <DialogContent>
        <>
          {Boolean(updatedPdfDocumentAckData?.error) && (
            <Alert sx={{ margin: '1rem 0' }} severity='error'>
              {computeErrorMessage(updatedPdfDocumentAckData?.error)}
            </Alert>
          )}
          <div id='alert-dialog-description'>
            <span className='alert-dialog-description__text'>{translate(RESOURCES_KEYS.ACK_CONTENT_TEXT)} :</span>
            <span className='alert-dialog-description__pdf-title'>
              <strong>{documentVersion.title}</strong>
            </span>
          </div>
          <FormControlLabel
            control={
              <Checkbox
                onChange={() => setIsConfirmationReceiptChecked(!isConfirmationReceiptChecked)}
                checked={isConfirmationReceiptChecked}
              />
            }
            label={translate(RESOURCES_KEYS.ACK_CONTENT_TEXT)}
          />
        </>
        {hasError && <Alert severity='error'>{translate(RESOURCES_KEYS.FIELD_REQUIRED)}</Alert>}
      </DialogContent>
      <DialogActions>
        <Button variant='outlined' onClick={handleClickCancel}>
          {translate(RESOURCES_KEYS.CANCEL)}
        </Button>
        <Button variant='contained' onClick={handleClickSend}>
          {translate(RESOURCES_KEYS.SEND)}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default Acknowledgment;
